import { useEffect, useRef } from 'react'

const FFT_SIZE = 128

// 録音ボタン周りの脈動 ring を音声振幅に連動させる hook.
// AnalyserNode の平均振幅 (0..1) を指数移動平均で均し、 ring 要素の transform/opacity を
// rAF で直接書く (= React 再レンダを発生させず 60fps の脈動を実現).
// 返り値の ref を脈動 ring 要素に attach する. stream 不在 / active=false では何もしない.
export const useAudioPulse = (stream: MediaStream | null, active: boolean) => {
  const ringRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const ring = ringRef.current
    if (!stream || !active || !ring) return

    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext
    const ctx = new AudioCtx()
    const source = ctx.createMediaStreamSource(stream)
    const analyser = ctx.createAnalyser()
    analyser.fftSize = FFT_SIZE
    source.connect(analyser)
    const buffer = new Uint8Array(analyser.frequencyBinCount)

    let smoothed = 0
    let phase = 0
    let raf = 0
    const tick = () => {
      analyser.getByteFrequencyData(buffer)
      let sum = 0
      for (let i = 0; i < buffer.length; i++) sum += buffer[i]
      const avg = sum / buffer.length / 255 // 0..1
      // attack を速めに取り (= 0.4) 声の立ち上がりに敏感に追従させる. ガタつきは残り 0.6 で均す.
      smoothed = smoothed * 0.6 + avg * 0.4
      // 無音時に「生きている」と分かる最小限の呼吸だけ残す (= 主役は音声反応. 単に大きくはしない).
      phase += 0.05
      const breathe = 0.02 * (0.5 + 0.5 * Math.sin(phase))
      // 音声感度を高く (係数 5.0) しつつ、 拡張は小さく抑える (= 上限 +0.2 → scale ~1.22).
      // ボタン本体と同程度の footprint に収め「デカく」見せない.
      const audioGain = Math.min(smoothed * 5.0, 0.2)
      ring.style.transform = `scale(${1 + breathe + audioGain})`
      // opacity も音声に敏感 (係数 3.4) に振り、 声の強弱が視覚的に出るようにする.
      ring.style.opacity = `${0.2 + Math.min(smoothed * 3.4, 0.5)}`
      raf = requestAnimationFrame(tick)
    }
    tick()

    return () => {
      cancelAnimationFrame(raf)
      source.disconnect()
      void ctx.close()
    }
  }, [stream, active])

  return ringRef
}
